import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// --- Clomiphene -----------------------------------------------------

const clomiphene: ContentBlock<"medication"> = {
  id: makeId("med-clomiphene"),
  kind: "medication",
  tags: ["medications", "her", "during-cycle", "iui", "pcos", "evidence-based"],
  title: "Clomid",
  subtitle: "Clomiphene citrate - oral ovulation induction medication",
  body: {
    genericName: "Clomiphene citrate",
    drugClass: "Selective estrogen receptor modulator (SERM)",
    purpose:
      "An oral medication that stimulates ovulation by blocking estrogen receptors in the hypothalamus. This tricks the brain into thinking estrogen is low, causing increased GnRH, FSH, and LH production, which stimulates follicle growth. First-line treatment for anovulatory infertility (especially PMOS (ex PCOS)) and also used in IUI cycles to produce 1-3 mature follicles. One of the oldest and most widely used fertility medications.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "50-150 mg daily for 5 days, starting on cycle day 2-5. Starting dose is usually 50 mg; may be increased by 50 mg increments in subsequent cycles if no response.",
    howToTake:
      "Step 1: Prepare - Confirm your starting day with your clinic (typically cycle day 3; some clinics use days 2-6 or 5-9). Have your tablets ready and set a daily alarm for consistency. | Step 2: Take medication - Swallow one to three tablets by mouth once daily for 5 consecutive days, at the same time each day, with or without food. | Step 3: After taking - Ovulation typically occurs 5-10 days after the last pill. Attend all scheduled ultrasound monitoring appointments so your clinic can track follicle growth and time a trigger shot if needed. Most clinics recommend no more than 6 cycles of clomiphene. | ⚠ Missed dose - Take the missed dose as soon as you remember the same day. If you don't remember until the next day, skip the missed dose and continue your regular schedule - do not double up. Contact your clinic so they can adjust monitoring if needed. | ⚠ When to call clinic - Call immediately if you experience visual disturbances (blurred vision, spots, or flashes), severe abdominal bloating or pain, or significant pelvic discomfort, as these may indicate ovarian hyperstimulation or a vision-related reaction requiring prompt evaluation.",
    sideEffects: [
      "Hot flashes",
      "Mood swings and irritability",
      "Headache",
      "Bloating",
      "Breast tenderness",
      "Visual disturbances (blurred vision, spots - discontinue and contact your doctor immediately)",
      "Cervical mucus thickening (anti-estrogenic effect)",
      "Endometrial thinning with prolonged use",
      "Multiple pregnancy risk (5-8% twins)",
    ],
    storage:
      "Store at room temperature (15-25°C / 59-77°F). Protect from light, heat, and moisture.",
    brandsByCountry: [
      { country: "US", brands: ["Clomid", "Serophene"] },
      { country: "UK", brands: ["Clomid"] },
      { country: "EU", brands: ["Clomid", "Pergotime"] },
      { country: "AU", brands: ["Clomid", "Serophene"] },
      {
        country: "FR",
        brands: ["Clomid", "Pergotime"],
        cisByBrand: {
          Clomid: "65338028",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (emc, AU CMI, MIMS Asia, Cipla).
    // Heavily genericized; documented major brands only.
    availability: [
      {
        name: "Clomid",
        manufacturer: "Sanofi",
        countries: ["US", "GB", "AU", "PH", "TH", "MY"],
      },
      {
        name: "Serophene",
        manufacturer: "Merck Serono",
        countries: ["CA", "AU"],
      },
      { name: "Duinum", countries: ["SG", "MY", "TH", "HK", "TW"] },
      {
        name: "Ovinum",
        manufacturer: "T.O. Chemicals",
        countries: ["TH", "MY", "PH", "VN", "SG"],
      },
      { name: "Profertil", manufacturer: "Lapi", countries: ["ID", "SG"] },
      { name: "Fertomid", manufacturer: "Cipla", countries: ["IN", "ZA"] },
      { name: "Clostilbegyt", manufacturer: "Egis", countries: ["MY"] },
      { name: "Ova-Mit", manufacturer: "Rotexmedica", countries: ["MY", "PH"] },
    ],
    aliases: ["Clomiphene", "Clomifene", "Clomifène"],
    forWhom: "her",
    frBody: {
      genericName: "Citrate de clomifène",
      drugClass: "Modulateur sélectif des récepteurs aux œstrogènes (SERM)",
      purpose:
        "Médicament oral qui stimule l'ovulation en bloquant les récepteurs aux œstrogènes au niveau de l'hypothalamus. Le cerveau réagit comme si le taux d'œstrogènes était bas et augmente la production de GnRH, de FSH et de LH, ce qui stimule la croissance folliculaire. Traitement de première intention de l'infertilité anovulatoire, notamment dans le PMOS (ex SOPK) ; également utilisé dans les cycles d'IIU (insémination intra-utérine) pour obtenir 1 à 3 follicules matures. C'est l'un des plus anciens et des plus utilisés des médicaments de fertilité.",
      dosageRanges:
        "50 à 150 mg par jour pendant 5 jours, à partir du 2e au 5e jour du cycle. La dose initiale est habituellement de 50 mg ; elle peut être augmentée par paliers de 50 mg lors des cycles suivants en l'absence de réponse.",
      howToTake:
        "Préparer - Confirmez le jour de début avec votre centre (généralement au 3e jour du cycle ; certains centres utilisent les jours 2-6 ou 5-9). Ayez vos comprimés à portée de main et programmez une alarme quotidienne pour la régularité. | Prendre le médicament - Avalez un à trois comprimés par voie orale une fois par jour pendant 5 jours consécutifs, à la même heure chaque jour, avec ou sans nourriture. | Après la prise - L'ovulation survient généralement 5 à 10 jours après la dernière prise. Assistez à toutes les échographies de suivi prévues pour que votre centre puisse contrôler la croissance folliculaire et programmer un déclencheur si besoin. La plupart des centres recommandent de ne pas dépasser 6 cycles de clomifène. | ⚠ Dose oubliée - Prenez la dose oubliée dès que vous y pensez le jour même. Si vous ne vous en souvenez que le lendemain, sautez-la et continuez votre rythme habituel - ne doublez jamais la dose. Prévenez votre centre pour qu'il puisse adapter le suivi. | ⚠ Quand contacter le centre - Appelez immédiatement votre équipe en cas de troubles visuels (vision floue, taches, flashes lumineux), de ballonnements ou douleurs abdominales sévères, ou de gêne pelvienne marquée, qui peuvent évoquer une hyperstimulation ovarienne ou une réaction visuelle nécessitant une évaluation rapide.",
      sideEffects: [
        "Bouffées de chaleur",
        "Sautes d'humeur et irritabilité",
        "Maux de tête",
        "Ballonnements",
        "Sensibilité des seins",
        "Troubles visuels (vision floue, taches - arrêter le traitement et contacter le médecin immédiatement)",
        "Épaississement de la glaire cervicale (effet anti-œstrogénique)",
        "Amincissement de l'endomètre en cas d'usage prolongé",
        "Risque de grossesses multiples (5 à 8 % de jumeaux)",
      ],
      storage:
        "À conserver à température ambiante (15-25 °C). Protéger de la lumière, de la chaleur et de l'humidité.",
    },
  },
};

// --- Letrozole ------------------------------------------------------

const letrozole: ContentBlock<"medication"> = {
  id: makeId("med-letrozole"),
  kind: "medication",
  tags: ["medications", "her", "during-cycle", "iui", "pcos", "evidence-based"],
  title: "Femara",
  subtitle: "Letrozole - aromatase inhibitor for ovulation induction",
  body: {
    genericName: "Letrozole",
    drugClass: "Aromatase inhibitor",
    purpose:
      "An aromatase inhibitor that induces ovulation by temporarily blocking estrogen production. The resulting low estrogen signals the brain to increase FSH output, stimulating follicle growth. Now considered first-line for ovulation induction in PMOS (ex PCOS) (per NICE and ASRM guidelines) due to higher live birth rates and lower multiple pregnancy rates compared to clomiphene. Also widely used in IUI cycles. Does not cause the anti-estrogenic effects on cervical mucus and endometrium seen with clomiphene.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "2.5-7.5 mg daily for 5 days, starting on cycle day 2-5. Starting dose is usually 2.5 mg; may be increased to 5 mg or 7.5 mg in subsequent cycles.",
    howToTake:
      "Step 1: Prepare - Confirm your starting day with your clinic (typically cycle day 3). Have your tablets ready and set a daily alarm for consistency. | Step 2: Take medication - Swallow tablets by mouth once daily for 5 consecutive days, at the same time each day, with or without food. | Step 3: After taking - Ovulation generally occurs 5-10 days after the last dose. Attend all scheduled ultrasound appointments; your clinic may add a trigger shot to time ovulation precisely. Note that letrozole is rapidly cleared from the body (half-life ~48 hours) and is no longer present at the time of ovulation or implantation. | ⚠ Missed dose - Take the missed dose as soon as you remember the same day. If you don't remember until the next day, skip the missed dose and continue your regular schedule - do not double up. Let your clinic know so they can account for it in monitoring. | ⚠ When to call clinic - Call if you experience severe abdominal pain, significant bloating, or difficulty breathing, as these may signal ovarian hyperstimulation and require prompt evaluation.",
    sideEffects: [
      "Hot flashes (usually milder than clomiphene)",
      "Fatigue",
      "Dizziness",
      "Headache",
      "Joint or muscle aches",
      "Nausea",
      "Lower multiple pregnancy rate than clomiphene (2-4% twins)",
    ],
    storage:
      "Store at room temperature (15-30°C / 59-86°F). Keep in the original packaging.",
    brandsByCountry: [
      { country: "US", brands: ["Femara"] },
      { country: "UK", brands: ["Femara", "Letrozole"] },
      { country: "EU", brands: ["Femara", "Letrozole"] },
      { country: "AU", brands: ["Femara", "Letrozole"] },
      {
        country: "FR",
        brands: ["Femara", "Letrozole Mylan", "Letrozole Sandoz"],
        cisByBrand: {
          Femara: "64492112",
          "Letrozole Sandoz": "65224722",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (TGA, Novartis, MIMS Asia). Femara
    // is the originator; heavily genericized.
    availability: [
      {
        name: "Femara",
        manufacturer: "Novartis",
        countries: ["US", "GB", "EU", "AU", "CA", "SG", "HK", "TH"],
      },
    ],
    aliases: ["Létrozole"],
    forWhom: "her",
    frBody: {
      genericName: "Létrozole",
      drugClass: "Inhibiteur de l'aromatase",
      purpose:
        "Inhibiteur de l'aromatase qui induit l'ovulation en bloquant temporairement la production d'œstrogènes. La baisse d'œstrogènes signale au cerveau d'augmenter la production de FSH, ce qui stimule la croissance folliculaire. Aujourd'hui considéré comme traitement de première intention pour l'induction de l'ovulation dans le PMOS (ex SOPK) en raison d'un taux de naissances vivantes plus élevé et d'un risque de grossesses multiples plus faible que sous citrate de clomifène. Également largement utilisé dans les cycles d'IIU (insémination intra-utérine), il ne provoque pas les effets anti-œstrogéniques sur la glaire cervicale et l'endomètre observés avec le clomifène.",
      dosageRanges:
        "2,5 à 7,5 mg par jour pendant 5 jours, à partir du 2e au 5e jour du cycle. La dose initiale est habituellement de 2,5 mg ; elle peut être augmentée à 5 mg ou 7,5 mg lors des cycles suivants.",
      howToTake:
        "Préparer - Confirmez le jour de début avec votre centre (généralement au 3e jour du cycle). Ayez vos comprimés à portée de main et programmez une alarme quotidienne pour la régularité. | Prendre le médicament - Avalez les comprimés par voie orale une fois par jour pendant 5 jours consécutifs, à la même heure chaque jour, avec ou sans nourriture. | Après la prise - L'ovulation survient généralement 5 à 10 jours après la dernière dose. Assistez à toutes les échographies de suivi prévues ; votre centre peut ajouter un déclencheur pour synchroniser précisément l'ovulation. À noter que le létrozole est rapidement éliminé (demi-vie ≈ 48 heures) et n'est plus présent au moment de l'ovulation ou de l'implantation. | ⚠ Dose oubliée - Prenez la dose oubliée dès que vous y pensez le jour même. Si vous ne vous en souvenez que le lendemain, sautez-la et continuez votre rythme habituel - ne doublez jamais la dose. Prévenez votre centre afin qu'il puisse adapter le suivi. | ⚠ Quand contacter le centre - Contactez votre équipe en cas de douleurs abdominales sévères, de ballonnements importants ou de difficultés respiratoires, qui peuvent évoquer une hyperstimulation ovarienne et nécessitent une évaluation rapide.",
      sideEffects: [
        "Bouffées de chaleur (généralement plus modérées qu'avec le clomifène)",
        "Fatigue",
        "Vertiges",
        "Maux de tête",
        "Douleurs articulaires ou musculaires",
        "Nausées",
        "Taux de grossesses multiples plus bas qu'avec le clomifène (2 à 4 % de jumeaux)",
      ],
      storage:
        "À conserver à température ambiante (15-30 °C). Garder dans l'emballage d'origine.",
    },
  },
};

registerBlock(clomiphene);
registerBlock(letrozole);

const anastrozole: ContentBlock<"medication"> = {
  id: makeId("med-anastrozole"),
  kind: "medication",
  tags: ["medications", "her", "his", "during-cycle", "ivf"],
  title: "Arimidex",
  subtitle:
    "Anastrozole - aromatase inhibitor for ovulation induction and male factor",
  body: {
    genericName: "Anastrozole",
    drugClass: "Non-steroidal aromatase inhibitor",
    purpose:
      "Anastrozole blocks the aromatase enzyme that converts androgens into estrogen, lowering estrogen levels. In women it can be used off-label for ovulation induction (an alternative to letrozole) and, during stimulation for fertility preservation in estrogen-sensitive breast cancer, to keep estrogen low while eggs are collected. In men, lowering estrogen reduces negative feedback on the pituitary, which can raise FSH, LH, and testosterone and improve sperm count and motility when the testosterone-to-estradiol ratio is low. Its licensed use is breast cancer in postmenopausal women, so all fertility uses are off-label and clinic-directed.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "For fertility uses, most commonly 1 mg by mouth once daily. For ovulation induction it is typically taken for about 5 days early in the cycle. For fertility preservation it is taken alongside gonadotropin stimulation under specialist supervision. For male factor, 1 mg daily or about 1 mg three times per week has been studied. All fertility dosing is off-label and set by your clinic.",
    howToTake:
      "Step 1: Confirm the plan - Take anastrozole exactly as your fertility clinic directs; fertility dosing differs from the licensed breast-cancer dose, and for ovulation induction it is taken only on specific cycle days. | Step 2: Take the tablet - Swallow the tablet whole with water at about the same time each day. It can be taken with or without food. | Step 3: Stay consistent - Keep to the schedule your clinic sets and attend any monitoring appointments. | ⚠ Missed dose - Take it as soon as you remember unless it is almost time for the next dose, then skip the missed dose; do not double up. | ⚠ When to call - Do not take anastrozole if you are or could be pregnant outside a supervised cycle, as it can harm a developing baby. Tell your clinic about other medicines (especially estrogen-containing products), and report severe joint pain, persistent low mood, or anything unusual.",
    sideEffects: [
      "Hot flushes and sweating",
      "Joint pain or stiffness",
      "Headache",
      "Tiredness or weakness",
      "Nausea",
      "Mood changes or low mood",
      "Reduced bone density with longer use",
    ],
    storage:
      "Store below 25°C (77°F), in the original packaging, protected from moisture and light, and out of reach of children.",
    forWhom: "both",
    aliases: ["Anastrozole", "Arimidex"],
    availability: [
      {
        name: "Arimidex",
        manufacturer: "AstraZeneca",
        countries: ["GB", "US", "EU"],
      },
      {
        name: "Anastrozole (generic)",
        countries: ["GB", "US", "EU", "CA", "AU"],
      },
    ],
    frBody: {
      genericName: "Anastrozole",
      drugClass: "Inhibiteur non stéroïdien de l'aromatase",
      purpose:
        "L'anastrozole bloque l'enzyme aromatase qui transforme les androgènes en œstrogènes, ce qui abaisse le taux d'œstrogènes. Chez la femme, il peut être utilisé hors AMM pour induire l'ovulation (alternative au létrozole) et, pendant la stimulation pour la préservation de la fertilité en cas de cancer du sein sensible aux œstrogènes, pour maintenir un faible taux d'œstrogènes pendant le recueil des ovocytes. Chez l'homme, la baisse des œstrogènes réduit le rétrocontrôle sur l'hypophyse, ce qui peut augmenter la FSH, la LH et la testostérone et améliorer le sperme lorsque le rapport testostérone/œstradiol est bas. Son usage autorisé est le cancer du sein chez la femme ménopausée ; tous les usages en fertilité sont hors AMM et dirigés par la clinique.",
      dosageRanges:
        "Pour la fertilité, le plus souvent 1 mg par voie orale une fois par jour. Pour l'induction de l'ovulation, généralement pendant environ 5 jours en début de cycle. Pour la préservation de la fertilité, en même temps que la stimulation, sous surveillance spécialisée. Pour l'infertilité masculine, 1 mg par jour ou environ 1 mg trois fois par semaine ont été étudiés. Toute posologie en fertilité est hors AMM et fixée par votre clinique.",
      howToTake:
        "Étape 1 : Confirmer le plan - Prenez l'anastrozole exactement comme votre clinique l'indique ; la posologie en fertilité diffère de la dose autorisée pour le cancer du sein, et pour l'induction de l'ovulation il se prend uniquement certains jours du cycle. | Étape 2 : Prendre le comprimé - Avalez le comprimé entier avec de l'eau à peu près à la même heure chaque jour. Avec ou sans nourriture. | Étape 3 : Rester régulière - Respectez le calendrier fixé par votre clinique et assistez aux contrôles. | ⚠ Dose oubliée - Prenez-la dès que vous y pensez, sauf s'il est presque l'heure de la suivante, auquel cas sautez la dose oubliée ; ne doublez pas. | ⚠ Quand appeler - Ne prenez pas d'anastrozole si vous êtes ou pourriez être enceinte hors d'un cycle surveillé, car il peut nuire au fœtus. Signalez les autres médicaments (surtout ceux contenant des œstrogènes) et tout symptôme inhabituel.",
      sideEffects: [
        "Bouffées de chaleur et sueurs",
        "Douleurs ou raideurs articulaires",
        "Maux de tête",
        "Fatigue ou faiblesse",
        "Nausées",
        "Changements d'humeur ou humeur basse",
        "Diminution de la densité osseuse en cas d'usage prolongé",
      ],
      storage:
        "Conserver à une température inférieure à 25°C (77°F), dans l'emballage d'origine, à l'abri de l'humidité et de la lumière, et hors de portée des enfants.",
    },
  },
};
registerBlock(anastrozole);
